import { useState } from "react";
import SheetInformation from "./SheetInformation";
import InformationColumns from "./InformationColumns";
import BufferColumns from "./BufferColumns";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronUp,
  faCopy,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { colors } from "../../global";
import FileToSpreadsheetModalSelect from "../Utils/FileToSpreadsheetModalSelect";

export default function FileAccordion({
  file,
  metadata,
  sheetList,
  onUpdateMetadata,
  onDeleteMetadata,
  onCopyMetadata,
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isOpenSelectingExcel, setIsOpenSelectingExcel] = useState(false);

  const [activeExcelField, setActiveExcelField] = useState(null);

  const [activeExcelIndex, setActiveExcelIndex] = useState(null);
  const [activeExcelType, setActiveExcelType] = useState(null);

  const [selectionType, setSelectionType] = useState("all");

  const excelSelectionTypeMap = {
    first_data_row: "row",
    service_filter_column: "column",
    last_data_row: "row",
    buffer_target_column: "column", // example for buffer columns
    // default: "all"
  };
  function openExcelModalForField(key) {
    const selectionType = excelSelectionTypeMap[key] || "all";
    setActiveExcelField(key);
    setActiveExcelIndex(null);
    setActiveExcelType(null);
    setSelectionType(selectionType);
    setIsOpenSelectingExcel(true);
  }

  function openExcelModalForIndex(index, type) {
    const key =
      type === "buffer" ? "buffer_target_column" : "information_column";
    const selectionType = excelSelectionTypeMap[key] || "all";
    setActiveExcelIndex(index);
    setActiveExcelType(type);
    setActiveExcelField(null);
    setSelectionType(selectionType);
    setIsOpenSelectingExcel(true);
  }

  function closeModal() {
    setIsOpenSelectingExcel(false);
    setActiveExcelField(null);
    setActiveExcelIndex(null);
    setSelectionType("all");
    setActiveExcelType(null);
  }

  function onSelectRange(value) {
    if (activeExcelField) {
      handleSheetChange(activeExcelField, value);
    } else if (activeExcelIndex !== null && activeExcelType !== null) {
      if (activeExcelType === "information") {
        handleInformationColumnChange(activeExcelIndex, "column", value);
      } else if (activeExcelType === "buffer") {
        handleBufferColumnChange(activeExcelIndex, "target_column", value);
      }
    }
    closeModal();
  }

  const handleSheetNameChange = (id, name) => {
    const updated = {
      ...metadata,
      sheet_information: {
        ...metadata.sheet_information,
        sheet_name: name,
        sheet_id: id,
      },
    };
    onUpdateMetadata(updated);
  };

  const handleOverwriteSheet = (value) => {
    const updated = {
      ...metadata,
      sheet_information: {
        ...metadata.sheet_information,
        overwrite_sheet: value,
      },
    };
    onUpdateMetadata(updated);
  };

  const handleSheetChange = (key, value) => {
    const updated = {
      ...metadata,
      sheet_information: {
        ...metadata.sheet_information,
        [key]: value,
      },
    };
    onUpdateMetadata(updated);
  };

  const handleInformationColumnChange = (index, field, value) => {
    const updatedInformationColumn = [...metadata.information_columns];
    updatedInformationColumn[index][field] = value;
    const updated = {
      ...metadata,
      information_columns: updatedInformationColumn,
    };
    onUpdateMetadata(updated);
  };

  const handleBufferColumnChange = (index, key, value) => {
    const updatedBuffers = [...metadata.buffer_columns];
    updatedBuffers[index][key] = value;
    const updated = { ...metadata, buffer_columns: updatedBuffers };
    onUpdateMetadata(updated);
  };

  const addInformationColumn = () => {
    const updated = {
      ...metadata,
      information_columns: [...metadata.information_columns, ""],
    };
    onUpdateMetadata(updated);
  };

  const removeInformationColumn = (index) => {
    const updatedColumns = metadata.information_columns.filter(
      (_, i) => i !== index
    );
    const updated = { ...metadata, information_columns: updatedColumns };
    onUpdateMetadata(updated);
  };

  const addBufferColumn = () => {
    const updated = {
      ...metadata,
      buffer_columns: [
        ...metadata.buffer_columns,
        {
          name: "",
          target_column: "",
          Item_Type: "",
          from: "",
          to: "",
          rename_column: "",
        },
      ],
    };
    onUpdateMetadata(updated);
  };

  const removeBufferColumn = (index) => {
    const updatedBuffers = metadata.buffer_columns.filter(
      (_, i) => i !== index
    );
    const updated = { ...metadata, buffer_columns: updatedBuffers };
    onUpdateMetadata(updated);
  };

  return (
    <>
      {isOpenSelectingExcel && file && (
        <FileToSpreadsheetModalSelect
          file={file}
          closeModal={closeModal}
          metadata={metadata}
          onSubmit={onSelectRange}
          selectionType={selectionType}
        />
      )}

      <div
        className="mb-4 overflow-hidden rounded-lg shadow-sm m-3 border"
        style={{
          borderColor: colors.primary,
          borderRadius: "0.5rem",
        }}
      >
        {/* Header */}
        <div
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex flex-wrap justify-between items-center p-3 gap-y-2 cursor-pointer rounded-lg"
          style={{ backgroundColor: colors.accent, color: colors.white }}
        >
          <span className="font-medium text-sm truncate max-w-[60%] sm:max-w-[80%]">
            {metadata.id}
          </span>

          <div className="flex flex-wrap gap-2 sm:gap-2 items-center">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onCopyMetadata(metadata.id);
              }}
              className="bg-white rounded-full w-8 h-8 flex items-center justify-center hover:opacity-90 transition-opacity"
              style={{ color: colors.primary }}
              aria-label="Copy metadata"
            >
              <FontAwesomeIcon icon={faCopy} />
            </button>

            <button
              onClick={(e) => {
                e.stopPropagation();
                onDeleteMetadata(metadata.id);
              }}
              className="bg-white rounded-full w-8 h-8 flex items-center justify-center hover:opacity-90 transition-opacity"
              style={{ color: colors.primary }}
              aria-label="Delete metadata"
            >
              <FontAwesomeIcon icon={faTrash} />
            </button>

            <button
              onClick={(e) => e.stopPropagation()}
              className="text-white w-8 h-8 flex items-center justify-center"
              aria-label={isExpanded ? "Collapse" : "Expand"}
            >
              <FontAwesomeIcon
                icon={isExpanded ? faChevronUp : faChevronDown}
              />
            </button>
          </div>
        </div>

        {/* Expanded content */}
        {isExpanded && (
          <div className="p-4 space-y-6 bg-gray-50">
            <SheetInformation
              sheetList={sheetList}
              metadata={metadata}
              handleSheetChange={handleSheetChange}
              handleSheetNameChange={handleSheetNameChange}
              handleOverwriteSheet={handleOverwriteSheet}
              openExcelModal={openExcelModalForField}
              excelSelectableFields={[
                "service_filter_column",
                "first_data_row",
                "last_data_row",
              ]}
            />

            <InformationColumns
              file={file}
              metadata={metadata}
              handleInformationColumnChange={handleInformationColumnChange}
              removeInformationColumn={removeInformationColumn}
              addInformationColumn={addInformationColumn}
              openExcelModal={(index) =>
                openExcelModalForIndex(index, "information")
              }
            />

            <BufferColumns
              metadata={metadata}
              handleBufferColumnChange={handleBufferColumnChange}
              removeBufferColumn={removeBufferColumn}
              addBufferColumn={addBufferColumn}
              openExcelModal={(index) =>
                openExcelModalForIndex(index, "buffer")
              }
            />
          </div>
        )}
      </div>
    </>
  );
}
